import React, { useEffect } from "react";
import PropTypes from "prop-types";

// logo (default) - you can override via prop logoSrc
import DefaultLogo from "../assets/images/logo/logo.png";

// social icons (place your icon files in src/assets/icons/)
// import fbIcon from "../assets/images/box-icon/facebook.png";
// import xIcon from "../assets/images/box-icon/twitter.png";
import telegramIcon from "../assets/images/box-icon/telegram.png";
// import instagramIcon from "../assets/images/box-icon/instagram.png";
// import youtubeIcon from "../assets/images/box-icon/youtube.png";

const useSmoothScroll = ({ offset = 0 } = {}) => {
  useEffect(() => {
    const handleClick = (e) => {
      const el = e.currentTarget;
      const href = el.getAttribute("href");
      if (!href || href.charAt(0) !== "#") return;

      const targetId = href.slice(1);
      const target = document.getElementById(targetId) || document.querySelector(href);
      if (!target) return;

      e.preventDefault();

      const targetRect = target.getBoundingClientRect();
      const absoluteElementTop = targetRect.top + window.pageYOffset;
      const scrollTo = Math.max(absoluteElementTop - offset, 0);

      window.scrollTo({
        top: scrollTo,
        behavior: "smooth",
      });
    };

    const delegatedHandler = (event) => {
      let node = event.target;
      while (node && node !== document) {
        if (node.tagName === "A" && node.classList.contains("page-scroll")) {
          handleClick({ currentTarget: node, preventDefault: event.preventDefault.bind(event) });
          break;
        }
        node = node.parentNode;
      }
    };

    document.addEventListener("click", delegatedHandler);
    return () => {
      document.removeEventListener("click", delegatedHandler);
    };
  }, [offset]);
};

const Footer = ({
  siteName = "Bit Wave Capital",
  logoSrc = DefaultLogo,
  logoHref = "/",
  menuLinks = [
    { label: "Home", href: "/" },
    { label: "about", href: "#about", isPageScroll: true },
    { label: "Services", href: "#Services", isPageScroll: true }, 
    { label: "why Choose", href: "#whychoose", isPageScroll: true }, 
    { label: "how It Works", href: "#howItWorks", isPageScroll: true }, 
    { label: "Technology", href: "#Technology", isPageScroll: true },
    { label: "Roadmap", href: "#roadmap", isPageScroll: true },
    { label: "Faq", href: "#faq", isPageScroll: true },
  ],
  socialLinks = [
    // { id: "facebook", href: "javascript:void(0)", label: "Facebook", icon: fbIcon },
    // { id: "x", href: "javascript:void(0)", label: "X / Twitter", icon: xIcon },
    { id: "telegram", href: "javascript:void(0)", label: "Telegram", icon: telegramIcon },
    // { id: "instagram", href: "javascript:void(0)", label: "Instagram", icon: instagramIcon },
    // { id: "youtube", href: "javascript:void(0)", label: "YouTube", icon: youtubeIcon },
  ],
  designer = { name: "Bit Wave Capital", href: "/", color: "#1af5fd" },
  startYear,
  scrollOffset = 0,
}) => {
  useSmoothScroll({ offset: scrollOffset });

  const currentYear = new Date().getFullYear();
  const yearText =
    startYear && Number(startYear) < currentYear ? `${startYear} - ${currentYear}` : `${currentYear}`;

  return (
    <footer id="footer" className="footer" role="contentinfo"> 
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-3">
            <div className="widget-logo">
                <a href={logoHref} aria-label={`${siteName} homepage`}>
                  <img id="logo_footer" src={logoSrc} alt={`${siteName} Logo`} />
                </a>
            </div>
          </div>
          <div className="col-lg-9">
            <p className="mb-5 text-white">Bit Wave Capital is a multi-asset trading platform focused on data-driven strategies and disciplined market execution.
              We aim to empower traders with insights, technology, and responsible trading practices.</p>
            <div className="widget-menu">
              <ul className="footer-menu">
                {menuLinks.map((link, i) => (
                  <li key={i}>
                    <a
                      className={link.isPageScroll ? "page-scroll" : undefined}
                      href={link.href}
                      aria-label={link.label}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="widget-social">
                <ul className="flex" aria-label="social links">
                  {socialLinks.map((s) => (
                    <li key={s.id}>
                      <a
                        href={s.href || "#"}
                        target="_blank"
                        rel={s.href && s.href !== "#" ? "noopener noreferrer" : undefined}
                        aria-label={s.label}
                        title={s.label}
                      >
                        {/* if s.icon is a React node (JSX) render it, else assume it's an image path/string */}
                        {React.isValidElement(s.icon) ? (
                          s.icon
                        ) : s.icon ? (
                          <img
                            src={s.icon}
                            alt={s.label}
                            width="24"
                            height="24"
                            style={{ display: "inline-block" }}
                          />
                        ) : (
                          <span className={s.iconClass} aria-hidden />
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
          </div>
        </div> 
        <div className="footer-bottom">
          <p>
            Copyright © {yearText} &nbsp;
            <a href={designer.href} style={{ color: designer.color || "#2aa3ae" }}>
              {designer.name}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  siteName: PropTypes.string,
  logoSrc: PropTypes.string,
  logoHref: PropTypes.string,
  menuLinks: PropTypes.arrayOf(PropTypes.object),
  socialLinks: PropTypes.arrayOf(PropTypes.object),
  designer: PropTypes.shape({
    name: PropTypes.string,
    href: PropTypes.string,
    color: PropTypes.string,
  }),
  startYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scrollOffset: PropTypes.number,
};

export default Footer;
